import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Sparkles, Circle } from 'lucide-react';

/**
 * Create Machine Dialog - Minimal, Structural
 * 
 * This dialog intentionally asks for ONLY:
 * 1. Machine Name - Display name
 * 2. Initial State Name - The first state node
 * 
 * NO DSL. NO effects. NO capabilities. NO guards. NO advanced options.
 * 
 * The user should feel like:
 * "I'm about to step into a machine that's about to exist."
 */
const CreateMachineDialog = ({ open, onOpenChange, onCreate, isCreating }) => {
  const [machineName, setMachineName] = useState('');
  const [initialStateName, setInitialStateName] = useState('Idle');
  const [errors, setErrors] = useState({});
  const machineNameRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMachineName('');
      setInitialStateName('Idle');
      setErrors({});
      setTimeout(() => machineNameRef.current?.focus(), 100);
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};
    
    if (!machineName.trim()) {
      newErrors.machineName = 'Machine name is required';
    } else if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(machineName)) {
      newErrors.machineName = 'Must start with letter, alphanumeric only';
    }
    
    if (!initialStateName.trim()) {
      newErrors.initialStateName = 'Initial state name is required';
    } else if (!/^[A-Za-z][A-Za-z0-9_]*$/.test(initialStateName)) {
      newErrors.initialStateName = 'Must start with letter, alphanumeric only';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    if (!validate()) return;
    
    // Create minimal Machine IR with just one initial state
    const machineIR = {
      name: machineName,
      initial: initialStateName,
      states: {
        [initialStateName]: {
          id: initialStateName,
          type: 'atomic',
          transitions: [],
          entry: [],
          exit: [],
          invariants: [],
          meta: {}
        }
      },
      events: [],
      effects: {},
      guards: {},
      invariants: [],
      meta: {}
    };
    
    onCreate(machineIR);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isCreating) {
      e.preventDefault();
      handleCreate();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#0a0e27] border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Sparkles className="w-5 h-5 text-blue-400" />
            <span>Create Machine</span>
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Start with a name and an initial state. Everything else grows from the graph.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Machine Name */}
          <div className="space-y-2">
            <Label htmlFor="machineName" className="text-sm font-medium">
              Machine Name
            </Label>
            <Input
              ref={machineNameRef}
              id="machineName"
              value={machineName}
              onChange={(e) => setMachineName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="MyStateMachine"
              className={`bg-black/30 border-white/10 focus:border-blue-500/50 ${
                errors.machineName ? 'border-red-500/50' : ''
              }`}
              data-testid="machine-name-input"
            />
            {errors.machineName && (
              <p className="text-xs text-red-400">{errors.machineName}</p>
            )}
          </div>

          {/* Initial State Name */}
          <div className="space-y-2">
            <Label htmlFor="initialState" className="text-sm font-medium flex items-center space-x-2">
              <span>Initial State Name</span>
              <span className="flex items-center text-[10px] text-green-500/70 font-normal">
                <Circle className="w-2 h-2 fill-green-500 text-green-500 mr-1" />
                will be marked initial
              </span>
            </Label>
            <Input
              id="initialState"
              value={initialStateName}
              onChange={(e) => setInitialStateName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Idle"
              className={`bg-black/30 border-white/10 focus:border-blue-500/50 ${
                errors.initialStateName ? 'border-red-500/50' : ''
              }`}
              data-testid="initial-state-input"
            />
            {errors.initialStateName && (
              <p className="text-xs text-red-400">{errors.initialStateName}</p>
            )}
          </div>

          {/* Visual Preview */}
          <div className="bg-black/40 border border-white/10 rounded-lg p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Preview</div>
            <div className="flex items-center justify-center">
              <div className="border-2 border-dashed border-blue-500/30 rounded-lg px-6 py-3 bg-blue-500/5">
                <div className="text-center">
                  <div className="text-xs text-green-400 mb-1">INITIAL</div>
                  <div className="font-mono text-sm text-white">
                    {initialStateName || 'State'}
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center mt-3 text-[10px] text-gray-500">
              Your machine starts here. Add states and transitions in the graph.
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !machineName.trim() || !initialStateName.trim()}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="create-machine-button"
          >
            {isCreating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create Machine'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateMachineDialog;
